/* eslint-disable @typescript-eslint/no-explicit-any */
import { type PlatformEnvironmentConfiguration } from './environment.interface';

type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

// keys that could lead to prototype pollution when merged from untrusted input
const FORBIDDEN_KEYS = Object.freeze(['__proto__', 'prototype', 'constructor']);

/**
 * Utility function for merging and deep freezing objects
 *
 * @param base BaseEnvironment
 * @param environment Optionally a DeepPartial object with overrides for base
 * @returns Frozen PlatformEnvironmentConfiguration
 */
export function createPlatformEnvironment<T extends PlatformEnvironmentConfiguration>(
  base: T,
  environment?: DeepPartial<T>,
): T {
  function deepMerge<T>(target: T, source?: DeepPartial<T>): T {
    if (!source) {
      return target;
    }

    const output = { ...target };

    for (const key in source) {
      if (FORBIDDEN_KEYS.includes(key)) {
        continue;
      }

      const sourceVal = source[key];
      const targetVal = (target as any)[key];

      if (typeof sourceVal === 'object' && sourceVal !== null && !Array.isArray(sourceVal)) {
        (output as any)[key] = deepMerge(
          typeof targetVal === 'object' && targetVal !== null ? targetVal : {},
          sourceVal,
        );
      } else {
        (output as any)[key] = sourceVal;
      }
    }

    return output;
  }

  // simple deepFreeze function
  function deepFreeze<T extends object>(x: T): T {
    Object.freeze(x);

    // iterate over the values and if there is a inner object freeze that one
    Object.values(x).forEach((x) => {
      if (typeof x === 'object' && x !== null) {
        deepFreeze(x);
      }
    });
    return x;
  }

  return deepFreeze(deepMerge(base, environment) as T);
}
